import React, { useState } from 'react';
import { FeedbackData } from '../../contexts/ParticipantContext';
import { BrButton, BrRadio, BrMessage, BrTextarea } from '@govbr-ds/react-components';

interface FeedbackFormProps {
    onSubmit: (feedback: FeedbackData) => void;
    isLoading?: boolean;
    isLastTask?: boolean;
}

function StarRating({
    value,
    onChange,
    label,
    lowLabel,
    highLabel
}: {
    value: number;
    onChange: (val: number) => void;
    label: string;
    lowLabel: string;
    highLabel: string;
}) {
    const [hover, setHover] = useState(0);

    return (
        <div className="mb-4">
            <label className="text-sm text-weight-medium text-gray-80 mb-2 d-block">{label}</label>
            <div className="d-flex align-items-center gap-2 mb-2">
                {[1, 2, 3, 4, 5].map((star) => (
                    <button
                        key={star}
                        type="button"
                        onMouseEnter={() => setHover(star)}
                        onMouseLeave={() => setHover(0)}
                        onClick={() => onChange(star)}
                        className={`border-0 bg-transparent p-1 transition-all focus:outline-none 
                        ${(hover || value) >= star ? 'text-warning' : 'text-gray-20'}`}
                        aria-label={`${star} estrela${star > 1 ? 's' : ''}`}
                        style={{ fontSize: '2rem', cursor: 'pointer' }}
                    >
                        <i className={`fas fa-star`}></i>
                    </button>
                ))}
            </div>
            <div className="d-flex justify-content-between text-xs text-gray-60">
                <span>{lowLabel}</span>
                <span>{highLabel}</span>
            </div>
        </div>
    );
}

export default function FeedbackForm({ onSubmit, isLoading, isLastTask }: FeedbackFormProps) {
    const [formData, setFormData] = useState<FeedbackData>({
        task_completion: 'complete',
        clarity_score: 0,
        ease_of_use_score: 0,
        visual_score: 0,
        suggestions: ''
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (formData.clarity_score === 0 || formData.ease_of_use_score === 0 || formData.visual_score === 0) {
            alert('Por favor, avalie todos os itens antes de continuar.');
            return;
        }
        onSubmit(formData);
    };

    const isValid = formData.clarity_score > 0 && formData.ease_of_use_score > 0 && formData.visual_score > 0;

    return (
        <form onSubmit={handleSubmit} className="d-flex flex-column gap-4">
            <BrMessage status="info" title="Avalie a Interface" message="Suas respostas são anônimas e nos ajudam a melhorar o sistema." />

            {/* Task Completion */}
            <fieldset>
                <legend className="text-weight-medium mb-2 text-sm text-gray-80">
                    Você conseguiu completar a tarefa proposta? *
                </legend>
                <div className="d-flex flex-column gap-2">
                    <BrRadio
                        id="completion-complete"
                        name="task_completion"
                        label="Sim, completamente"
                        value="complete"
                        checked={formData.task_completion === 'complete'}
                        onChange={() => setFormData(prev => ({ ...prev, task_completion: 'complete' }))}
                    />
                    <BrRadio
                        id="completion-partial"
                        name="task_completion"
                        label="Sim, mas com dificuldade"
                        value="partial"
                        checked={formData.task_completion === 'partial'}
                        onChange={() => setFormData(prev => ({ ...prev, task_completion: 'partial' }))}
                    />
                    <BrRadio
                        id="completion-failed"
                        name="task_completion"
                        label="Não consegui"
                        value="failed"
                        checked={formData.task_completion === 'failed'}
                        onChange={() => setFormData(prev => ({ ...prev, task_completion: 'failed' }))}
                    />
                </div>
            </fieldset>

            {/* Clarity Rating */}
            <StarRating
                value={formData.clarity_score}
                onChange={(val) => setFormData(prev => ({ ...prev, clarity_score: val }))}
                label="As informações apresentadas estavam claras? *"
                lowLabel="Nada claras"
                highLabel="Muito claras"
            />

            {/* Ease of Use Rating */}
            <StarRating
                value={formData.ease_of_use_score}
                onChange={(val) => setFormData(prev => ({ ...prev, ease_of_use_score: val }))}
                label="Foi fácil encontrar o que você precisava? *"
                lowLabel="Muito difícil"
                highLabel="Muito fácil"
            />

            {/* Visual Rating */}
            <StarRating
                value={formData.visual_score}
                onChange={(val) => setFormData(prev => ({ ...prev, visual_score: val }))}
                label="A aparência da interface foi agradável? *"
                lowLabel="Nada agradável"
                highLabel="Muito agradável"
            />

            {/* Suggestions */}
            <div>
                <BrTextarea
                    id="suggestions"
                    label="O que poderia ser melhorado? (opcional)"
                    placeholder="Digite suas sugestões aqui..."
                    value={formData.suggestions}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFormData(prev => ({ ...prev, suggestions: e.target.value }))}
                />
            </div>

            {/* Submit Button */}
            <BrButton
                primary
                block
                type="submit"
                disabled={!isValid || isLoading}
            >
                {isLoading
                    ? 'Enviando...'
                    : isLastTask
                        ? 'Enviar e Finalizar'
                        : 'Enviar e Próxima Tarefa'
                }
            </BrButton>
        </form>
    );
}
